import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import EmptyState from "../../components/EmptyState";
import CockpitHero from "../../components/CockpitHero";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Plus, ScrollText, Sparkles } from "lucide-react";

export default function MyPapersPage() {
  const navigate = useNavigate();
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/papers/draft/me").then((r) => setPapers(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto pb-16">
        <CockpitHero
          testId="papers-hero"
          eyebrow="My Papers"
          eyebrowIcon={ScrollText}
          title="Position"
          titleAccent="papers"
          titleTail="."
          description="All your drafts and submitted papers, in one editorial folio."
          primaryCta={{
            label: "New paper",
            to: "/dashboard/papers/new",
            icon: Plus,
            testid: "new-paper-button",
          }}
        />

        <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="papers-list">
          {loading ? (
            <div className="col-span-2"><LoadingSkeleton variant="cards" count={4} /></div>
          ) : papers.length === 0 ? (
            <div className="col-span-2">
              <EmptyState
                icon={ScrollText}
                eyebrow="No papers yet"
                title="Draft your first position paper"
                description="Open a new paper, brief your country's stance and let Diplomexa AI suggest clauses, refine your arguments, and check for policy gaps."
                primary={{ label: "Start writing", testId: "empty-start-paper", onClick: () => navigate("/dashboard/papers/new") }}
                secondary={{ label: "See examples", onClick: () => navigate("/dashboard/lms/mun-prep") }}
                testId="papers-empty-state"
              />
            </div>
          ) : papers.map((p) => (
            <Link
              key={p.id}
              to={`/dashboard/papers/${p.id}`}
              className="rounded-xl bg-white border border-slate-200 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all"
              data-testid={`paper-card-${p.id}`}
            >
              <div className="flex items-start justify-between">
                <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${
                  p.status === "submitted" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                }`}>
                  {p.status === "submitted" ? "Submitted" : "Draft"}
                </span>
                <span className="text-[10px] text-slate-400 font-mono">v{p.version}</span>
              </div>
              <h3 className="mt-3 text-base font-semibold tracking-tight text-[#0A192F]">{p.title || "Untitled"}</h3>
              <p className="mt-1 text-xs text-slate-500">
                {p.country || "—"} · Updated {new Date(p.updated_at).toLocaleDateString()}
              </p>
            </Link>
          ))}
        </div>

        {!loading && papers.length > 0 && (
          <div className="mt-8 rounded-2xl bg-[#0A192F] text-white p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-[#D4AF37]" />
              <p className="text-sm">Need help? The Diplomexa AI Prep Assistant is one click away.</p>
            </div>
            <Link to="/dashboard/ai-prep">
              <Button className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]">
                Open AI Prep
              </Button>
            </Link>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
